import { useTranslation } from "react-i18next";
import { Tabs, TabsList, TabsTrigger } from "@sportscanner/ui";
import { colorForSport } from "./sport-color";
import type { Layout, LayoutSelection } from "./derive-layout";
import { formatCourtLabel } from "./helpers";
import type { MeSport } from "@/lib/me";

interface Props {
  layout: Layout;
  selection: LayoutSelection;
  setParam: (key: string, value: string | null) => void;
}

/**
 * Renders the courts/sports selection control for the current layout. Lifted
 * out of the calendar variants so the /bookings page can show it in its own
 * header row.
 *
 * Returns null when the layout has nothing to choose (single sport, single
 * uniform-schedule scope).
 */
export default function ScopeChooser({ layout, selection, setParam }: Props) {
  const { t } = useTranslation();

  if (layout.kind === "single") return null;

  if (layout.kind === "courtTabs") {
    return (
      <Tabs
        value={
          layout.tabs.find((tab) => tab.courtId === selection.activeCourtId)
            ?.id ?? layout.tabs[0].id
        }
        onValueChange={(id) => {
          const next = layout.tabs.find((tab) => tab.id === id);
          setParam("court", next?.courtId ?? null);
        }}
      >
        <CourtTabsList tabs={layout.tabs} />
      </Tabs>
    );
  }

  if (layout.kind === "combined") {
    return (
      <SportFilterChips
        sports={layout.scope.sports}
        activeSportId={selection.sportFilter}
        onChange={(value) => setParam("sportFilter", value)}
      />
    );
  }

  // sportTabs — render the sport row; if the active sport has nested court
  // tabs, render them below.
  const activeSport =
    layout.tabs.find((tab) => tab.sportId === selection.activeSportId) ??
    layout.tabs[0];

  return (
    <div className="flex flex-col gap-2">
      <Tabs
        value={activeSport.id}
        onValueChange={(id) => {
          const next = layout.tabs.find((tab) => tab.id === id);
          if (!next) return;
          // Court IDs are scoped to the previously-selected sport — clear them.
          setParam("court", null);
          setParam("sport", next.sportId);
        }}
      >
        <TabsList className="bg-white border border-neutral-200 h-auto p-1 flex-wrap">
          {layout.tabs.map((tab) => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className="data-[state=active]:bg-primary-600 data-[state=active]:text-white px-3 py-1.5"
            >
              {t(`sports.${tab.sportName}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {activeSport.inner.kind === "courtTabs" && (
        <Tabs
          value={
            activeSport.inner.tabs.find(
              (tab) => tab.courtId === selection.activeCourtId,
            )?.id ?? activeSport.inner.tabs[0].id
          }
          onValueChange={(id) => {
            const inner =
              activeSport.inner.kind === "courtTabs"
                ? activeSport.inner.tabs.find((tab) => tab.id === id)
                : undefined;
            setParam("court", inner?.courtId ?? null);
          }}
        >
          <CourtTabsList tabs={activeSport.inner.tabs} />
        </Tabs>
      )}
    </div>
  );
}

function CourtTabsList({
  tabs,
}: {
  tabs: { id: string; courtName: string }[];
}) {
  const { t } = useTranslation();
  return (
    <TabsList className="bg-white border border-neutral-200 h-auto p-1 flex-wrap">
      {tabs.map((tab) => (
        <TabsTrigger
          key={tab.id}
          value={tab.id}
          className="data-[state=active]:bg-primary-600 data-[state=active]:text-white px-3 py-1.5"
        >
          {formatCourtLabel(tab.courtName, t("bookings.form.court"))}
        </TabsTrigger>
      ))}
    </TabsList>
  );
}

function SportFilterChips({
  sports,
  activeSportId,
  onChange,
}: {
  sports: MeSport[];
  activeSportId: string | null;
  onChange: (sportId: string | null) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="inline-flex flex-wrap items-center gap-2 bg-white border border-neutral-200 rounded-full p-1">
      <ChipButton
        active={activeSportId === null}
        onClick={() => onChange(null)}
      >
        {t("bookings.calendar.allSports")}
      </ChipButton>
      {sports.map((s) => {
        const c = colorForSport(s.id, sports);
        const active = activeSportId === s.id;
        return (
          <ChipButton
            key={s.id}
            active={active}
            onClick={() => onChange(s.id)}
            activeClasses={`${c.badgeBg} ${c.badgeText}`}
          >
            <span
              className={`inline-block w-2 h-2 rounded-full mr-1.5 ${
                active ? "bg-white/80" : c.dot
              }`}
            />
            {t(`sports.${s.name}`)}
          </ChipButton>
        );
      })}
    </div>
  );
}

function ChipButton({
  active,
  onClick,
  activeClasses,
  children,
}: {
  active: boolean;
  onClick: () => void;
  activeClasses?: string;
  children: React.ReactNode;
}) {
  const base =
    "inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-full transition-colors cursor-pointer";
  if (active) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${base} ${activeClasses ?? "bg-primary-600 text-white"}`}
      >
        {children}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} bg-white text-neutral-600 hover:bg-neutral-50`}
    >
      {children}
    </button>
  );
}
